import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Space, Tag } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import StandardTable from '../../../components/common/StandardTable';
import { useGetMatClntWarehouses, useGetMatClntWhInventory, useGetVwMatClntRequestItemOutstanding } from '../../../hooks/genericHooks';
import type { ColumnsType } from 'antd/es/table';
import type { MatClntIssueRequest, MatClntIssueRequestItem, MatWarehouses, VwMatClntWhInventory, VwMatClntRequestItemOutstanding } from '../../../types';
import { fetchSqlData } from '../../../services/api';
import Button from '../../../components/common/Button';

const { Title, Text } = Typography;

type ItemRow = MatClntIssueRequestItem & { description?: string | null; issued_qty?: number; outstanding_qty?: number };

export default function MatClntIssueRequestDetails() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation() as any;
  const idParam = params?.id as string | undefined;
  const refNoFromState = location?.state?.ref_no as string | undefined;

  const [refNo, setRefNo] = useState<string | undefined>(refNoFromState);
  const [headerRecord, setHeaderRecord] = useState<MatClntIssueRequest | undefined>(undefined);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [loadingItems, setLoadingItems] = useState<boolean>(false);

  // Warehouses and inventory for enrichment
  const { data: whData = [] } = useGetMatClntWarehouses() as any;
  const warehouses: MatWarehouses[] = Array.isArray(whData) ? whData : (whData?.data ?? []);
  const whMap = useMemo(() => {
    const m = new Map<number, string>();
    (warehouses || []).forEach(w => m.set(w.id, w.name));
    return m;
  }, [warehouses]);

  const { data: invData = [] } = useGetMatClntWhInventory() as any;
  const invRows: VwMatClntWhInventory[] = Array.isArray(invData) ? invData : (invData?.data ?? []);
  const invDescMap = useMemo(() => {
    const m = new Map<string, { description?: string | null; unit?: string | null; size_text?: string | null }>();
    (invRows || []).forEach(r => {
      if (!m.has(r.itemcode)) m.set(r.itemcode, { description: r.description, unit: r.unit, size_text: (r as any)?.size_text ?? null });
    });
    return m;
  }, [invRows]);

  // Outstanding/Issued data per request item
  const { data: outData = [] } = useGetVwMatClntRequestItemOutstanding() as any;
  const outMapByItemId = useMemo(() => {
    const m = new Map<number, { issued_qty: number; outstanding_qty: number }>();
    const all: VwMatClntRequestItemOutstanding[] = Array.isArray(outData) ? (outData as any) : (outData?.data ?? []);
    const sel = String(refNo || '').trim();
    (all || []).forEach(r => {
      if (sel && String(r.ref_no || '').trim() !== sel) return;
      if (typeof r.item_id === 'number') m.set(r.item_id, { issued_qty: Number(r.issued_qty || 0), outstanding_qty: Number(r.outstanding_qty || 0) });
    });
    return m;
  }, [outData, refNo]);

  // Resolve header and ref_no
  useEffect(() => {
    let mounted = true;
    const resolveHeader = async () => {
      try {
        let header: MatClntIssueRequest | undefined = undefined;
        if (idParam && /^\d+$/.test(idParam)) {
          const resById = await fetchSqlData('mat_clnt_issue_requests', 'id', Number(idParam));
          const rowsById = Array.isArray(resById) ? resById : (resById?.data ?? []);
          header = rowsById?.[0] as MatClntIssueRequest | undefined;
        }
        if (!header && refNoFromState) {
          const resByRef = await fetchSqlData('mat_clnt_issue_requests', 'ref_no', String(refNoFromState));
          const rowsByRef = Array.isArray(resByRef) ? resByRef : (resByRef?.data ?? []);
          header = rowsByRef?.[0] as MatClntIssueRequest | undefined;
        }
        if (!header && idParam && !/^\d+$/.test(idParam)) {
          const resByRef2 = await fetchSqlData('mat_clnt_issue_requests', 'ref_no', String(idParam));
          const rowsByRef2 = Array.isArray(resByRef2) ? resByRef2 : (resByRef2?.data ?? []);
          header = rowsByRef2?.[0] as MatClntIssueRequest | undefined;
        }
        if (mounted && header) {
          setHeaderRecord(header);
          if (!refNoFromState && header?.ref_no) setRefNo(String(header.ref_no));
          if (refNoFromState && !refNo) setRefNo(String(refNoFromState));
        } else if (mounted && refNoFromState && !refNo) {
          setRefNo(String(refNoFromState));
        }
      } catch (e) {
        if (mounted && refNoFromState && !refNo) setRefNo(String(refNoFromState));
      }
    };
    resolveHeader();
    return () => { mounted = false; };
  }, [idParam, refNoFromState, refNo]);

  // Fetch items by ref_no when available
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      if (!refNo) return;
      try {
        setLoadingItems(true);
        const res = await fetchSqlData('mat_clnt_issue_request_items', 'ref_no', String(refNo));
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        const enriched: ItemRow[] = (rows || []).map((r: MatClntIssueRequestItem) => {
          const info = invDescMap.get(r.itemcode);
          const descOnly = info?.description || r.notes || null;
          const sz = (r as any)?.size_text ?? info?.size_text ?? null;
          const issuedOutstanding = outMapByItemId.get(r.id) || { issued_qty: 0, outstanding_qty: 0 };
          return { ...r, description: descOnly, size_text: sz, issued_qty: issuedOutstanding.issued_qty, outstanding_qty: issuedOutstanding.outstanding_qty } as ItemRow;
        });
        if (mounted) setItems(enriched);
      } finally {
        if (mounted) setLoadingItems(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [refNo, invDescMap, outMapByItemId]);

  const statusVal = headerRecord?.status as string | undefined;
  const statusTag = statusVal ? (
    <Tag color={
      statusVal === 'pending' ? 'gold' :
      statusVal === 'approved' ? 'blue' :
      statusVal === 'partially_issued' ? 'purple' :
      statusVal === 'issued' ? 'green' :
      statusVal === 'rejected' ? 'red' : 'default'
    }>{statusVal}</Tag>
  ) : null;

  const columns: ColumnsType<ItemRow> = useMemo(() => [
    { title: 'Itemcode', dataIndex: 'itemcode', key: 'itemcode', width: 120 },
    { title: 'Description', dataIndex: 'description', key: 'description', ellipsis: true, width: 300 },
    { title: 'Size', dataIndex: 'size_text', key: 'size_text', width: 120 },
    { title: 'Unit', dataIndex: 'unit', key: 'unit', width: 90 },
    { title: 'Requested', dataIndex: 'requested_qty', key: 'requested_qty', width: 120, align: 'right' },
    { title: 'Issued', dataIndex: 'issued_qty', key: 'issued_qty', width: 110, align: 'right', render: (v: any) => Number(v || 0) },
    { title: 'Outstanding', dataIndex: 'outstanding_qty', key: 'outstanding_qty', width: 120, align: 'right', render: (v: any) => Number(v || 0) },
    { title: 'Warehouse', dataIndex: 'warehouse_id', key: 'warehouse_id', width: 180, render: (v: number | null | undefined) => (v != null ? (whMap.get(v) || v) : '-') },
  ], [whMap]);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} onClick={() => navigate(-1)} type="text" aria-label="Back" />
            <div>
              <Title level={4} style={{ margin: 0 }}>Client Issue Request Details</Title>
              <Text type="secondary">Ref No: {refNo ?? '...'}</Text>
            </div>
          </Space>
        </Col>
      </Row>

      {/* Header info in one line */}
      <Row style={{ marginBottom: 8 }}>
        <Col span={24}>
          <Space size={24} wrap>
            <Text><strong>Ref No:</strong> {headerRecord?.ref_no || refNo || ''}</Text>
            <Text><strong>Request Date:</strong> {headerRecord?.req_date ? dayjs(headerRecord.req_date).format('YYYY-MM-DD') : ''}</Text>
            <span><Text strong>Status:</Text> {statusTag}</span>
            <Text><strong>Requester (Code):</strong> {headerRecord?.requester || ''}</Text>
            <Text><strong>Requester Name:</strong> {headerRecord?.requester_name || ''}</Text>
          </Space>
        </Col>
      </Row>

      {/* Notes and optional fields */}
      <Row style={{ marginBottom: 12 }}>
        <Col span={24}>
          <Space size={24} wrap>
            <Text><strong>Notes:</strong> {headerRecord?.notes || ''}</Text>
            {headerRecord?.material_group ? (<Text><strong>Group:</strong> {headerRecord.material_group}</Text>) : null}
            {headerRecord?.section ? (<Text><strong>Section:</strong> {headerRecord.section}</Text>) : null}
            {headerRecord?.line_no ? (<Text><strong>Line No:</strong> {headerRecord.line_no}</Text>) : null}
            {headerRecord?.spool_id != null ? (<Text><strong>Spool ID:</strong> {headerRecord.spool_id}</Text>) : null}
            {headerRecord?.workpermit_ref_num ? (<Text><strong>WP Ref:</strong> {headerRecord.workpermit_ref_num}</Text>) : null}
            {headerRecord?.activity_code || headerRecord?.activity_name ? (
              <Text><strong>Activity:</strong> {(headerRecord?.activity_code || '') + (headerRecord?.activity_name ? ` - ${headerRecord.activity_name}` : '')}</Text>
            ) : null}
          </Space>
        </Col>
      </Row>

      <StandardTable<ItemRow>
        columns={columns as any}
        dataSource={items}
        rowKey={(r) => String(r.id)}
        loading={loadingItems}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
    </Card>
  );
}
